import React, { useState, useEffect } from 'react';
import './App.css';
import { AnimatePresence } from 'framer-motion';
import { SpcProvider, useSpc } from './context/SpcContext';
import { Logo, motion, SpcLoader, CyclingText } from './components/ui';

const BOOT_MESSAGES = ['Loading the Clubhouse…', "Grabbing today's sessions…", 'Checking your benefits…', 'Almost there…'];
import { CalendarDays, Store, Gift, UserRound, MessageSquare, Trophy, Menu, LogOut, Download, Flag, X, CreditCard, CheckCircle2, Loader2, MessageCircle, Target, ShoppingBag, ChevronRight, KeyRound, HelpCircle, ShieldAlert, Sparkles, WifiOff, Footprints } from 'lucide-react';
import Auth from './components/Auth';
import Toast from './components/Toast';
import Notifications from './components/Notifications';
import Celebration from './components/Celebration';
import Welcome from './components/Welcome';
import PinResetGate from './components/PinResetGate';
import BreakingNews from './components/BreakingNews';
import WhatsappJoinPopup from './components/WhatsappJoinPopup';
import RaceHub from './components/RaceHub';
import Modals from './components/Modals';
import Home from './components/views/Home';
import Book from './components/views/Book';
import Sell from './components/views/Sell';
import Benefits from './components/views/Benefits';
import Profile from './components/views/Profile';
import Leaderboard from './components/views/Leaderboard';
import Games from './components/views/Games';
import Challenges from './components/views/Challenges';
import Poll from './components/views/Poll';
import Shop from './components/views/Shop';
import Resources from './components/views/Resources';
import CoachHub from './components/views/CoachHub';
import Feed from './components/views/Feed';
import Parkrun from './components/views/Parkrun';

const TABS = [
  { id: 'profile', label: 'My SPC', Icon: UserRound, standout: true },
  { id: 'book', label: 'Book', Icon: CalendarDays },
  { id: 'leaderboard', label: 'Points', Icon: Trophy },
  { id: 'benefits', label: 'Benefits', Icon: Gift },
  { id: 'challenges', label: 'Challenges', Icon: Target }
];
const MORE_VIEWS = ['community', 'poll', 'coachhub', 'shop', 'parkrun'];

function Ambient() {
  return (<><div className="spc-bg" /><div className="spc-vignette" /><div className="spc-grain" /></>);
}

function MoreMenu({ open, onClose, items }) {
  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div className="more-backdrop" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={onClose} />
          <motion.div className="more-sheet" data-testid="more-menu"
            initial={{ opacity: 0, y: 24, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 24, scale: 0.98 }}
            transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}>
            <div className="more-head"><strong>More</strong><button className="more-x" onClick={onClose} aria-label="Close"><X size={18} /></button></div>
            <div className="more-items">
              {items.map(it => (
                <button key={it.id} data-testid={`more-${it.id}`} className={`more-item ${it.active ? 'active' : ''} ${it.danger ? 'danger' : ''}`} onClick={() => { it.onClick(); onClose(); }}>
                  <span className="more-ic"><it.Icon size={18} /></span>{it.label}
                  {it.badge > 0 && <span className="more-item-badge" data-testid={`more-badge-${it.id}`}>{it.badge}</span>}
                </button>
              ))}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}

function PaymentSuccess({ checkPaymentStatus }) {
  const [paid, setPaid] = useState(false);
  const [failed, setFailed] = useState(false);
  const [kind, setKind] = useState('membership');
  useEffect(() => {
    const sid = new URLSearchParams(window.location.search).get('session_id');
    if (!sid) { setFailed(true); return; }
    let tries = 0, timer;
    const poll = async () => {
      tries += 1;
      try {
        const { data } = await checkPaymentStatus(sid);
        if (data.kind) setKind(data.kind);
        if (data.payment_status === 'paid') {
          setPaid(true);
          if (data.kind === 'session_booking' && data.session_booking_id) {
            localStorage.setItem('spc_pending_booking_confirm', data.session_booking_id);
          } else if (data.kind === 'block_booking' && data.series_id) {
            localStorage.setItem('spc_pending_block_confirm', data.series_id);
          }
          setTimeout(() => window.location.replace((data.kind === 'session_booking' || data.kind === 'block_booking') ? '/?nav=book' : '/'), 1400);
          return;
        }
      } catch (e) {}
      if (tries >= 15) { setFailed(true); return; }
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(timer);
  }, [checkPaymentStatus]);
  const isSession = kind === 'session_booking' || kind === 'block_booking';
  return (
    <div className="pending-wrap" data-testid="payment-success-screen">
      <div className="card pending-card">
        <div className="pending-icon" style={{ color: paid ? '#7bd88f' : undefined }}>{paid ? <CheckCircle2 size={30} /> : failed ? '!' : <SpcLoader size={30} />}</div>
        <div className="eyebrow">{isSession ? 'Session booking' : 'Membership'}</div>
        <h2>{paid ? "You're in! 🎉" : failed ? 'Still processing' : 'Confirming your payment…'}</h2>
        <p>{paid ? (isSession ? "You're booked in! Taking you to your bookings…" : 'Your SPC membership is active. Taking you to the Clubhouse…') : failed ? 'Your payment may still be processing. If you were charged, your access will unlock shortly.' : 'Hang tight while we confirm your payment with Stripe.'}</p>
        {failed && <button className="btn" style={{ marginTop: 16 }} onClick={() => window.location.replace('/')}>Continue</button>}
      </div>
    </div>
  );
}

function PaymentCancel({ startCheckout }) {
  return (
    <div className="pending-wrap" data-testid="payment-cancel-screen">
      <div className="card pending-card">
        <div className="pending-icon">×</div>
        <div className="eyebrow">Payment cancelled</div>
        <h2>Membership not activated</h2>
        <p>No worries — your payment wasn't completed. Your SPC Clubhouse access unlocks as soon as you join for £29/year.</p>
        <button className="btn" data-testid="retry-payment-btn" style={{ marginTop: 16 }} onClick={startCheckout}><CreditCard size={16} /> Pay &amp; join · £29/year</button>
        <button className="btn secondary" style={{ marginTop: 10 }} onClick={() => window.location.replace('/')}>Back</button>
      </div>
    </div>
  );
}

function ResetPassword({ resetPassword }) {
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [done, setDone] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const token = new URLSearchParams(window.location.search).get('token');
  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (!token) { setError('This reset link is missing its token.'); return; }
    if (password.length < 6) { setError('Use at least 6 characters.'); return; }
    if (password !== confirm) { setError("Passwords don't match."); return; }
    setSubmitting(true);
    const ok = await resetPassword(token, password);
    setSubmitting(false);
    if (ok) setDone(true);
  };
  if (done) {
    return (
      <div className="pending-wrap" data-testid="reset-password-screen">
        <div className="card pending-card">
          <div className="pending-icon"><CheckCircle2 size={30} /></div>
          <div className="eyebrow">Password updated</div>
          <h2>You're all set</h2>
          <p>Your password has been changed. Sign in with your new password.</p>
          <button className="btn" style={{ marginTop: 16 }} onClick={() => window.location.replace('/')}>Back to sign in</button>
        </div>
      </div>
    );
  }
  return (
    <div className="pending-wrap" data-testid="reset-password-screen">
      <div className="card pending-card">
        <div className="pending-icon"><KeyRound size={26} /></div>
        <div className="eyebrow">Reset password</div>
        <h2>Choose a new password</h2>
        <form className="form" onSubmit={submit} style={{ marginTop: 12, textAlign: 'left' }}>
          <label>New password<input data-testid="reset-password-input" type="password" minLength={6} required value={password} onChange={(e) => { setPassword(e.target.value); setError(''); }} placeholder="Minimum 6 characters" /></label>
          <label>Confirm password<input data-testid="reset-password-confirm-input" type="password" minLength={6} required value={confirm} onChange={(e) => { setConfirm(e.target.value); setError(''); }} placeholder="Re-enter your new password" /></label>
          {error && <div className="notice" data-testid="reset-password-error">{error}</div>}
          <button className="btn" data-testid="reset-password-submit-btn" style={{ marginTop: 4 }} disabled={submitting}>{submitting ? 'Updating…' : 'Update password'}</button>
        </form>
      </div>
    </div>
  );
}

function MembershipGate({ user, startCheckout, logout }) {
  const lapsed = user.billingStatus === 'Lapsed' || user.billingStatus === 'Payment failed';
  // Admin manually paused this member — they still have a paid, valid membership,
  // so never show a payment wall for this; just explain the pause.
  if (user.status === 'Paused' && !lapsed) {
    return (
      <div className="pending-wrap" data-testid="membership-gate">
        <div className="card pending-card">
          <div className="pending-icon"><ShieldAlert size={26} /></div>
          <div className="eyebrow">Account paused</div>
          <h2>Your access has been paused</h2>
          <p>Your SPC Clubhouse access has been paused by the club{user.membershipRenewalDate ? <> — your membership itself is still valid until <strong>{new Date(user.membershipRenewalDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</strong></> : ''}. No payment is needed; get in touch with the club if you think this is a mistake.</p>
          <button className="btn secondary" style={{ marginTop: 16 }} onClick={logout} data-testid="paused-logout-btn">Log out</button>
        </div>
      </div>
    );
  }
  return (
    <div className="pending-wrap" data-testid="membership-gate">
      <div className="card pending-card">
        <div className="pending-icon"><CreditCard size={26} /></div>
        <div className="eyebrow">{lapsed ? 'Welcome back' : `One step left, ${user.name.split(' ')[0]}`}</div>
        <h2>{lapsed ? 'Renew your SPC membership' : 'Activate your SPC membership'}</h2>
        <p>{lapsed
          ? <>Your membership has lapsed. Renew for <strong>£29/year</strong> to restore full access to the Clubhouse.</>
          : <>Your account is created. Join the SPC Clubhouse for <strong>£29 for the year</strong> — one fee, no extras. Paying unlocks everything instantly, no waiting for approval.</>}</p>
        <div className="notice">{lapsed ? 'Your subscription is no longer active — renew to get back in.' : `Signed in as ${user.email}`}</div>
        <button className="btn" data-testid="pay-join-btn" style={{ marginTop: 16 }} onClick={startCheckout}><CreditCard size={16} /> {lapsed ? 'Renew' : 'Pay & join'} · £29/year</button>
        <button className="btn secondary" style={{ marginTop: 10 }} onClick={logout}>Log out</button>
      </div>
    </div>
  );
}

function ConnectionError({ onRetry, onLogout }) {
  const [retrying, setRetrying] = useState(false);
  const retry = () => { setRetrying(true); onRetry(); };
  return (
    <div className="pending-wrap" data-testid="connection-error-screen">
      <div className="card pending-card">
        <div className="pending-icon"><WifiOff size={26} /></div>
        <div className="eyebrow">Still signed in</div>
        <h2>Connection is slow or unavailable</h2>
        <p>You're still signed in — we just couldn't load your Clubhouse data over this connection. Check your network and try again.</p>
        <button className="btn" style={{ marginTop: 16 }} onClick={retry} disabled={retrying} data-testid="connection-retry-btn">
          {retrying ? <Loader2 size={16} className="spin" /> : null} {retrying ? 'Retrying…' : 'Try again'}
        </button>
        <button className="btn secondary" style={{ marginTop: 10 }} onClick={onLogout} data-testid="connection-logout-btn">Log out</button>
      </div>
    </div>
  );
}

function Shell() {
  const { user, logout, openModal, state, booting, connectionError, retryBoot, openRaceHub, installApp, startCheckout, checkPaymentStatus, resetPassword, pendingView, consumePendingView, busy } = useSpc();
  const [view, setView] = useState('home');
  const [moreOpen, setMoreOpen] = useState(false);
  const approved = user && user.approvalStatus === 'approved' && user.status === 'Active';
  useEffect(() => { setView('home'); }, [user?.id]);
  // Marketplace temporarily parked (23 Oct 2026) while testing whether it's why some members'
  // work computers block the site (corporate filters sometimes flag "shopping" category domains).
  // Remove this guard + restore the nav item below to bring it back.
  const go = (id) => { setView(id === 'community' ? 'home' : id); setMoreOpen(false); window.scrollTo({ top: 0, behavior: 'smooth' }); };

  // Deep-link on load from push notifications (e.g. /?nav=book)
  useEffect(() => {
    if (!user || !approved) return;
    const params = new URLSearchParams(window.location.search);
    const nav = params.get('nav');
    const valid = ['home', 'book', 'leaderboard', 'profile', 'shop', 'benefits', 'challenges', 'poll', 'coachhub'];
    if (nav && valid.includes(nav)) {
      go(nav);
      params.delete('nav');
      const qs = params.toString();
      window.history.replaceState({}, '', window.location.pathname + (qs ? `?${qs}` : ''));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, approved]);

  // In-app notification click → switch view
  useEffect(() => {
    if (pendingView) { go(pendingView); consumePendingView(); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingView]);

  const unreadMsgs = (state.conversations || []).reduce((n, c) => n + (c.unread || 0), 0);

  const path = window.location.pathname;
  if (path.startsWith('/payment/success')) return (<><Ambient /><PaymentSuccess checkPaymentStatus={checkPaymentStatus} /></>);
  if (path.startsWith('/payment/cancel')) return (<><Ambient /><PaymentCancel startCheckout={startCheckout} /><Toast /></>);
  if (path.startsWith('/reset-password')) return (<><Ambient /><ResetPassword resetPassword={resetPassword} /><Toast /></>);

  if (booting) return (<><Ambient /><div className="boot-screen" data-testid="boot-screen"><SpcLoader size={72} flash /><CyclingText messages={BOOT_MESSAGES} /></div></>);
  if (connectionError && !user) return (<><Ambient /><ConnectionError onRetry={retryBoot} onLogout={logout} /><Toast /></>);
  if (!user) return (<><Ambient />{busy > 0 && <div className="global-loading-bar" data-testid="global-loading-bar" />}<Auth /><Toast /></>);
  if (!approved) return (<><Ambient /><MembershipGate user={user} startCheckout={startCheckout} logout={logout} /><Toast /></>);
  if (user.requiresPinReset) return (<><Ambient /><PinResetGate /><Toast /></>);

  const initials = (user.name || 'S').split(' ').map(x => x[0]).slice(0, 2).join('').toUpperCase();
  const views = { home: <Home go={go} />, book: <Book go={go} />, community: <Sell go={go} />, leaderboard: <Leaderboard />, games: <Games />, challenges: <Challenges />, poll: <Poll />, coachhub: <CoachHub />, benefits: <Benefits go={go} />, shop: <Shop />, resources: <Resources />, feed: <Feed />, profile: <Profile go={go} />, parkrun: <Parkrun /> };
  const moreActive = MORE_VIEWS.includes(view);
  const isLeader = user.role === 'Admin' || user.role === 'Coach' || user.role === 'Run Leader' || (user.permissions || []).includes('view_polls') || !!user.canScan;
  const canManageMembers = user.role !== 'Admin' && (user.permissions || []).includes('manage_members');
  const moreItems = [
    // Club Feed parked — do not release yet. Re-add this item when ready.
    // Marketplace parked 23 Oct 2026 — testing whether it's the reason some members' work
    // computers block the site. Re-add this item (and the `go()` guard above) when ready:
    // { id: 'community', label: 'Marketplace', Icon: Store, active: view === 'community', onClick: () => go('community') },
    { id: 'shop', label: 'Club Shops', Icon: ShoppingBag, active: view === 'shop', onClick: () => go('shop') },
    // Resource Library parked — do not release yet. Re-add this item when ready.
    // Games/Arcade parked — do not release yet. Re-add this item when ready.
    { id: 'aiCoach', label: 'Ask Smartie', Icon: Sparkles, onClick: () => openModal('aiCoach') },
    ...(isLeader ? [{ id: 'coachhub', label: 'Coaches / Leaders', Icon: ShieldAlert, active: view === 'coachhub' || view === 'poll', onClick: () => go('coachhub') }] : []),
    ...(canManageMembers ? [{ id: 'managemembers', label: 'Manage members', Icon: UserRound, onClick: () => openModal('adminMembers') }] : []),
    ...(user.role !== 'Admin' ? [{ id: 'whatsapp', label: 'WhatsApp group', Icon: MessageCircle, onClick: () => { const url = state?.settings?.whatsappUrl; if (url) window.open(url, '_blank', 'noopener'); else openModal('help'); } }] : []),
    { id: 'messages', label: 'Messages', Icon: MessageSquare, onClick: () => openModal('messages') },
    { id: 'racehub', label: 'Race Hub', Icon: Flag, onClick: () => openRaceHub('view') },
    { id: 'parkrun', label: 'Parkrun', Icon: Footprints, active: view === 'parkrun', onClick: () => go('parkrun') },
    { id: 'help', label: 'Help', Icon: HelpCircle, onClick: () => openModal('help') },
    { id: 'install', label: 'Install app', Icon: Download, onClick: () => installApp() },
    { id: 'logout', label: 'Log out', Icon: LogOut, danger: true, onClick: () => logout() },
  ];

  return (
    <>
      <Ambient />
      {busy > 0 && <div className="global-loading-bar" data-testid="global-loading-bar" />}
      <div className="app">
        <header className="topbar">
          <div className="topbar-inner">
            <button className={`brand brand-btn ${view === 'home' ? 'active' : ''}`} data-testid="brand-home" onClick={() => go('home')} aria-label="Go to Home" title="Go to Home">
              <div className="brand-logo"><Logo size={40} glow={false} /></div>
              <div><h1>Clubhouse</h1><small>Smart Performance Coaching</small></div>
            </button>
            <nav className="top-nav">
              {TABS.map(t => (
                <button data-testid={`nav-${t.id}`} key={t.id} className={`${view === t.id ? 'active' : ''} ${t.standout ? 'nav-standout' : ''}`} onClick={() => go(t.id)}>
                  {view === t.id && <motion.span layoutId="navGlow" className="nav-glow" transition={{ type: 'spring', stiffness: 380, damping: 32 }} />}
                  <t.Icon size={15} />{t.label}
                </button>
              ))}
              <button data-testid="nav-more" className={moreActive ? 'active' : ''} onClick={() => setMoreOpen(true)}>
                <Menu size={15} />More
              </button>
            </nav>
            <div className="user-menu">
              <button className="notif-btn" data-testid="header-ask-smartie-btn" onClick={() => openModal('aiCoach')} aria-label="Ask Smartie">
                <Sparkles size={19} />
              </button>
              <button className="notif-btn" data-testid="messages-btn" onClick={() => openModal('messages')} aria-label="Messages">
                <MessageSquare size={19} />
                {unreadMsgs > 0 && <span className="notif-badge" data-testid="messages-badge">{unreadMsgs}</span>}
              </button>
              <Notifications />
              <button
                className="user-chip user-chip-btn"
                data-testid="header-user-chip"
                onClick={() => openModal('myAccount')}
                aria-label="Open My Account"
                title="My Account"
              >
                <div className="user-avatar" data-testid="header-avatar">{user.avatar ? <img src={user.avatar} alt={user.name} className="avatar-img" /> : initials}</div>
                <div className="user-chip-info">
                  <strong data-testid="current-user-name">{user.name}</strong>
                  <span className="user-chip-role">{user.role === 'Admin' ? 'Admin' : ((user.roleNames && user.roleNames.length) ? user.roleNames.join(' · ') : (user.tier || 'Member'))}</span>
                </div>
                <ChevronRight size={16} className="user-chip-chevron" aria-hidden="true" />
              </button>
            </div>
          </div>
        </header>
        <main>
          <AnimatePresence mode="wait">
            <motion.div key={view}
              initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}>
              {views[view]}
            </motion.div>
          </AnimatePresence>
        </main>
      </div>

      <nav className="tabs">
        {TABS.map(t => (
          <button key={t.id} data-testid={`tab-${t.id}`} className={`tab ${view === t.id ? 'active' : ''} ${t.standout ? 'tab-standout' : ''}`} onClick={() => go(t.id)}>
            {view === t.id && <motion.span layoutId="tabGlow" className="tab-glow" transition={{ type: 'spring', stiffness: 380, damping: 32 }} />}
            <t.Icon size={18} />{t.label}
          </button>
        ))}
        <button data-testid="tab-more" className={`tab ${moreActive ? 'active' : ''}`} onClick={() => setMoreOpen(true)}>
          <Menu size={18} />More
        </button>
      </nav>

      <MoreMenu open={moreOpen} onClose={() => setMoreOpen(false)} items={moreItems} />
      <RaceHub />
      <Modals />
      <Celebration />
      <Welcome />
      <BreakingNews />
      <WhatsappJoinPopup />
      <Toast />
    </>
  );
}

function App() {
  return (<SpcProvider><Shell /></SpcProvider>);
}
export default App;
